<script lang="ts">
  import { LAYOUTS } from '@multiview/shared';
  import { engine, PIP_OPACITIES, PIP_SIZES } from '../lib/player/engine.svelte.ts';
  import { onMount } from 'svelte';
  import type { Channel } from '@multiview/shared';
  import { remote } from '../lib/input/remote.ts';
  import LiveFixtures from './LiveFixtures.svelte';
  import ChannelPicker from './ChannelPicker.svelte';
  import { fixtureAngle } from '../lib/fixtureAngle.ts';
  import type { Fixture } from '../lib/api/client.ts';

  /** "This game too": joins the running picture, the menu closes to show it. */
  function addGame(fixture: Fixture, channelId: number) {
    engine.addAngle(fixtureAngle(fixture, channelId));
    onClose();
  }

  /**
   * The full channel list, open while picking: for a match whose suggestions
   * miss the right channel, or (fixture null) for any stream at all.
   */
  let picker = $state<{ fixture: Fixture | null; replace?: { id: string; label: string } } | null>(null);

  // Registered after App's handler, so it runs first: BACK leaves the channel
  // list before it closes the whole menu.
  onMount(() => remote.subscribe((action) => {
    if (action.type !== 'back' || picker === null) return false;
    picker = null;
    return true;
  }));

  function addChannel(channel: Channel) {
    const fixture = picker?.fixture ?? null;
    const replace = picker?.replace;
    picker = null;
    if (replace) {
      engine.changeChannel(replace.id, channel);
      return;
    }
    if (fixture) {
      addGame(fixture, channel.id);
      return;
    }
    engine.addAngle({
      id: `draft-${channel.id}-${Date.now()}`,
      label: channel.name,
      channelId: channel.id,
      role: 'both',
      syncOffsetMs: 0,
      preferredQuality: 'auto',
      fixtureProvider: null,
      fixtureExternalId: null,
    });
    onClose();
  }

  interface Props { onClose: () => void; onExit: () => void; onInfo: () => void }
  const { onClose, onExit, onInfo }: Props = $props();

  const visible = $derived(engine.visibleAngles);

  const AUTO_MODES = [
    { value: 'off', label: 'Aus' },
    { value: 'interval', label: 'Nach Zeit' },
    { value: 'excitement', label: 'Bei Jubel' },
    { value: 'both', label: 'Beides' },
  ] as const;

  // Without the audio graph there is nothing to listen to, so the cheer-based
  // modes would silently do nothing.
  const autoModes = $derived(
    engine.audioDisabledReason ? AUTO_MODES.filter((m) => m.value === 'off' || m.value === 'interval') : AUTO_MODES,
  );

  const PIP_CORNERS = [
    { label: '↖ oben links', x: 0, y: 0 },
    { label: '↗ oben rechts', x: 1, y: 0 },
    { label: '↙ unten links', x: 0, y: 1 },
    { label: '↘ unten rechts', x: 1, y: 1 },
  ] as const;

  const INTERVALS = [
    { seconds: 30, label: '30 s' },
    { seconds: 60, label: '1 min' },
    { seconds: 120, label: '2 min' },
    { seconds: 300, label: '5 min' },
    { seconds: 600, label: '10 min' },
    { seconds: 900, label: '15 min' },
    { seconds: 1800, label: '30 min' },
  ] as const;

  // Ticks only while the overlay is open; the countdown is all it drives.
  let now = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (now = Date.now()), 1000);
    return () => clearInterval(id);
  });
  const countdown = $derived.by(() => {
    if (engine.autoNextAt === null) return null;
    const left = Math.max(0, Math.round((engine.autoNextAt - now) / 1000));
    return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  });

  const zeit = (ms: number) => new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
</script>

<div class="overlay" role="dialog" aria-label="Multiview-Steuerung">
  <header>
    <h2>{engine.name || 'Multiview'}</h2>
    <div class="actions">
      <button data-focusable data-focus-default onclick={onClose}>Schließen</button>
      <button data-focusable onclick={onInfo}>Spielinfos</button>
      <button data-focusable onclick={onExit}>Zur Übersicht</button>
    </div>
  </header>

  <section class="perspectives">
    <h3>Perspektiven</h3>
    <ul class="angles">
      {#each engine.angles as angle, i (angle.id)}
        {@const stats = engine.statsFor(angle.id)}
        {@const visibleIndex = visible.findIndex((a) => a.id === angle.id)}
        <li class:is-main={angle.id === engine.mainAngleId}>
          <span class="key">{visibleIndex >= 0 ? visibleIndex + 1 : '–'}</span>

          <span class="info">
            <span class="name">{angle.label}</span>
            <span class="meta faint">
              {angle.role === 'audio' ? 'nur Ton' : angle.role === 'video' ? 'stumm' : 'Bild + Ton'}
              {#if stats}
                · {stats.width}×{stats.height} · {stats.mode ?? '—'}
                {#if stats.droppedFrames > 0}· <span class="drop">{stats.droppedFrames} Drops</span>{/if}
              {/if}
            </span>
          </span>

          <span class="controls">
            {#if angle.role !== 'audio'}
              <button
                data-focusable
                disabled={angle.id === engine.mainAngleId}
                onclick={() => engine.setMain(angle.id)}
              >Haupt</button>
            {/if}

            {#if angle.role !== 'video'}
              <button
                data-focusable
                class:active={angle.id === engine.audioAngleId}
                onclick={() => engine.setAudioAngle(angle.id)}
              >♪ Ton</button>
            {/if}

            <span class="sync">
              <button
                data-focusable
                aria-label={`${angle.label}: Verzögerung verringern`}
                onclick={() => { engine.setFocused(angle.id); engine.nudgeSync(-500); }}
              >−</button>
              <span class="offset mono">{(angle.syncOffsetMs / 1000).toFixed(1)}s</span>
              <button
                data-focusable
                aria-label={`${angle.label}: Verzögerung erhöhen`}
                onclick={() => { engine.setFocused(angle.id); engine.nudgeSync(500); }}
              >+</button>
            </span>

            <button
              data-focusable
              aria-label={`${angle.label}: anderen Sender wählen`}
              onclick={() => { picker = { fixture: null, replace: { id: angle.id, label: angle.label } }; }}
            >Sender</button>

            {#if engine.angles.length > 1}
              <button
                data-focusable
                aria-label={`${angle.label} entfernen`}
                onclick={() => engine.removeAngle(angle.id)}
              >✕</button>
            {/if}
          </span>
        </li>
      {/each}
    </ul>
    <div class="add-game">
      {#if picker}
        <ChannelPicker
          title={picker.replace
            ? `Neuer Sender für ${picker.replace.label}`
            : picker.fixture ? `Sender für ${picker.fixture.homeTeam} – ${picker.fixture.awayTeam}` : 'Sender hinzufügen'}
          onPick={addChannel}
          onCancel={() => { picker = null; }}
          usedChannelIds={engine.angles.map((a) => a.channelId)}
        />
      {:else}
        <LiveFixtures
          title="Spiel hinzufügen"
          alwaysChoose
          onAdd={addGame}
          onOther={(fixture) => { picker = { fixture }; }}
          usedChannelIds={engine.angles.map((a) => a.channelId)}
        />
        <button class="any" data-focusable onclick={() => { picker = { fixture: null }; }}>＋ Beliebigen Sender hinzufügen …</button>
      {/if}
    </div>
    <p class="hint faint">
      Verzögerung schiebt eine Perspektive zeitlich zurück. Der langsamste Feed bleibt bei 0,0 s
      und ist die Referenz — alles andere wird auf ihn hin verzögert.
    </p>
  </section>

  <div class="side">
    <section class="automatic">
      <h3>Automatisch umschalten</h3>
      <div class="auto">
        <div class="modes">
          {#each autoModes as mode (mode.value)}
            <button
              data-focusable
              class:active={engine.autoMode === mode.value}
              onclick={() => engine.setAutoMode(mode.value)}
            >{mode.label}</button>
          {/each}
        </div>

        <label class="toggle">
          <input
            type="checkbox"
            data-focusable
            checked={engine.goalSwitching}
            onchange={(e) => engine.setGoalSwitching(e.currentTarget.checked)}
          />
          <span>Bei Tor umschalten</span>
        </label>

      </div>

      {#if engine.autoMode === 'interval' || engine.autoMode === 'both'}
        <div class="interval" role="group" aria-label="Wechsel alle">
          <span class="faint">alle</span>
          {#each INTERVALS as option (option.seconds)}
            <button
              data-focusable
              class:active={engine.autoIntervalSeconds === option.seconds}
              onclick={() => engine.setAutoInterval(option.seconds)}
            >{option.label}</button>
          {/each}
        </div>
        <div class="next">
          {#if countdown}<span class="faint">Nächster Wechsel in <span class="mono">{countdown}</span></span>{/if}
          <button data-focusable onclick={() => engine.autoSwitchNow()}>Jetzt wechseln</button>
        </div>
        <p class="hint faint">
          Wechselt das Hauptbild reihum durch die Perspektiven. Schaltest du selbst um oder springt
          ein Tor dazwischen, beginnt die Zeit von vorn. Die Einstellung gilt auch für das nächste Spiel.
        </p>
      {/if}

      {#if engine.autoMode === 'excitement' || engine.autoMode === 'both'}
        <p class="hint faint">
          Erkennt Jubel am Tonpegel: steigt eine Perspektive deutlich über ihren eigenen
          Normalpegel, wird sie zum Hauptbild. Das trifft auch Großchancen, Elfmeter und Karten —
          also genau die anderen Momente, die man sehen will. Der Ton braucht einen Moment, um
          einen Normalpegel zu lernen.
        </p>
      {/if}

      {#if engine.goalSwitching}
        <p class="hint faint">
          Nutzt den Spielstand-Dienst. Jedes Tor wird zurückgehalten, bis dein Bild diesen
          Moment erreicht hat — es wird also nichts verraten. Dafür muss der Perspektive in der
          Übersicht ein Spiel zugeordnet sein.
          {#if engine.pendingGoals.length > 0}
            <strong>{engine.pendingGoals.length} Tor(e) warten auf das Bild.</strong>
          {/if}
        </p>
      {/if}

      {#if engine.autoLog.length > 0}
        <ul class="log">
          {#each engine.autoLog as entry (entry.at + entry.angleId)}
            <li>
              <span class="faint mono">{zeit(entry.at)}</span>
              <span>{entry.label}</span>
              <span class="faint">
                {#if entry.reason === 'goal'}Tor {entry.detail}
                {:else if entry.reason === 'excitement'}Jubel +{entry.deltaDb?.toFixed(1)} dB
                {:else}Zeittakt{/if}
              </span>
            </li>
          {/each}
        </ul>
      {/if}
    </section>

    <section class="layout">
      <h3>Layout</h3>
      <div class="layouts">
        {#each engine.availableLayouts as id (id)}
          <button
            data-focusable
            class:active={id === engine.effectiveLayout}
            onclick={() => engine.setLayout(id)}
          >{LAYOUTS[id].label}</button>
        {/each}
      </div>

      {#if LAYOUTS[engine.effectiveLayout].pip}
        <div class="pip-controls">
          <span class="faint">Kleines Bild</span>
          <div class="layouts">
            {#each PIP_CORNERS as corner (corner.label)}
              <button
                data-focusable
                class:active={engine.pip.x === corner.x && engine.pip.y === corner.y}
                onclick={() => engine.setPip({ x: corner.x, y: corner.y })}
              >{corner.label}</button>
            {/each}
          </div>
          <div class="layouts">
            {#each PIP_SIZES as size (size.value)}
              <button
                data-focusable
                class:active={engine.pip.size === size.value}
                onclick={() => engine.setPip({ size: size.value })}
              >{size.label}</button>
            {/each}
          </div>
          <div class="layouts">
            {#each PIP_OPACITIES as opacity (opacity)}
              <button
                data-focusable
                class:active={engine.pip.opacity === opacity}
                onclick={() => engine.setPip({ opacity })}
              >{Math.round(opacity * 100)} %</button>
            {/each}
          </div>
        </div>
        <p class="hint faint">Am PC lässt sich das kleine Bild mit der Maus frei verschieben. Ein Klick darauf tauscht es mit dem großen.</p>
      {/if}
    </section>
  </div>

  <section class="keys">
    <h3>Fernbedienung</h3>
    <dl>
      <div><dt>◀ ▶</dt><dd>Hauptstream durchschalten</dd></div>
      <div><dt>▲ ▼</dt><dd>Layout wechseln</dd></div>
      <div><dt>1 – 9</dt><dd>Perspektive direkt wählen</dd></div>
      <div><dt>OK</dt><dd>Dieses Menü</dd></div>
      <div><dt>Info / i</dt><dd>Spielinfos: Ereignisse, Tabelle, Aufstellung</dd></div>
      <div><dt>Grün</dt><dd>Ton auf fokussierte Kachel</dd></div>
      <div><dt>Gelb / Blau</dt><dd>Verzögerung −0,5 s / +0,5 s</dd></div>
      <div><dt>Rot</dt><dd>Technik-Anzeige</dd></div>
      <div><dt>Zurück</dt><dd>Übersicht</dd></div>
    </dl>
  </section>
</div>

<style>
  .overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    gap: var(--pad);
    padding: var(--safe-inset) calc(var(--safe-inset) + var(--pad));
    background: var(--bg-overlay);
    backdrop-filter: blur(6px);
    overflow-y: auto;
  }

  /*
   * Two columns wherever there is room, so the whole menu fits one TV screen:
   * D-pad focus does scroll, but a menu that has to be scrolled hides half of
   * what it offers.
   */
  @media (min-width: 900px) {
    .overlay {
      display: grid;
      grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
      grid-template-areas:
        "head head"
        "perspectives side"
        "keys keys";
      align-content: start;
      column-gap: calc(var(--pad) * 1.5);
    }

    header { grid-area: head; }
    .perspectives { grid-area: perspectives; }
    .side { grid-area: side; }
    .keys { grid-area: keys; }
  }

  .add-game { margin-top: var(--gap); display: flex; flex-direction: column; gap: var(--gap); align-items: stretch; }
  .add-game .any { align-self: flex-start; }

  .side { display: flex; flex-direction: column; gap: var(--pad); min-width: 0; }

  /* There is no remote on a phone. */
  :global(:root[data-platform="mobile"]) .keys { display: none; }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--pad);
    flex-wrap: wrap;
  }

  h2 { margin: 0; font-size: var(--font-xl); }
  h3 { margin: 0 0 0.6em; font-size: var(--font-base); color: var(--text-dim); font-weight: 600; }

  .actions { display: flex; gap: var(--gap); }

  .angles { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--gap); }

  .angles li {
    display: flex;
    align-items: center;
    gap: var(--gap);
    padding: calc(var(--gap) * 0.75);
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-left: 3px solid transparent;
    border-radius: var(--radius);
    flex-wrap: wrap;
  }

  .angles li.is-main { border-left-color: var(--accent); }

  .key {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2em; height: 2em;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.1);
    font-variant-numeric: tabular-nums;
  }

  .info { display: flex; flex-direction: column; gap: 2px; flex: 1 1 10em; min-width: 0; }
  .name { font-weight: 600; }
  .meta { font-size: var(--font-sm); }
  .drop { color: var(--warn); }

  .controls { display: flex; align-items: center; gap: var(--gap); flex-wrap: wrap; }
  .controls button.active { border-color: var(--audio); color: var(--audio); }
  .controls button:disabled { opacity: 0.45; cursor: default; }

  .sync { display: inline-flex; align-items: center; gap: 4px; }
  .sync button { min-width: 2.2em; text-align: center; }
  .offset { min-width: 3.4em; text-align: center; color: var(--text-dim); }

  .hint { margin: var(--gap) 0 0; font-size: var(--font-sm); max-width: 60ch; }

  .auto { display: flex; align-items: center; gap: var(--pad); flex-wrap: wrap; }
  .modes { display: flex; gap: var(--gap); flex-wrap: wrap; }
  .modes button.active { border-color: var(--accent); color: var(--accent); }
  .interval { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 8px; font-size: var(--font-sm); }
  .next { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 8px; font-size: var(--font-sm); }
  .toggle { display: inline-flex; align-items: center; gap: 8px; font-size: var(--font-sm); }
  .interval button.active { border-color: var(--accent); color: var(--accent); }

  .log { list-style: none; margin: var(--gap) 0 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
  .log li { display: flex; gap: var(--gap); font-size: var(--font-sm); }

  .layouts { display: flex; gap: var(--gap); flex-wrap: wrap; }
  .layouts button.active { border-color: var(--accent); color: var(--accent); }
  .pip-controls { display: flex; align-items: center; gap: var(--pad); flex-wrap: wrap; margin-top: var(--gap); font-size: var(--font-sm); }

  .keys dl { display: grid; grid-template-columns: repeat(auto-fill, minmax(19em, 1fr)); gap: 6px var(--pad); margin: 0; }
  .keys dl > div { display: flex; gap: var(--gap); }
  .keys dt { min-width: 5em; color: var(--text-dim); }
  .keys dd { margin: 0; }
</style>
